/**
 * recreateRasterSource — full MapLibre raster source recreation.
 *
 * Use this instead of `RasterTileSource.setTiles([newUrl])` when the new URL
 * may produce different imagery than the old one (scope switch, tenant switch,
 * farm clip change). MapLibre's tile cache is keyed by `(sourceId, z, x, y)` —
 * NOT by URL — so `setTiles` only invalidates pending fetches; already-painted
 * 256×256 tiles continue to render their stale pixels for areas that fall
 * outside the new scope and therefore never trigger a fresh fetch.
 *
 * AK-119 hotfix (continuation of AK-118 — the previous fix excluded raster
 * sources on the incorrect reasoning «raster tiles do not carry features that
 * can leak»). Founder symptom on prod (2026-05-13 17:55Z): selected farm
 * «Руно» → coloured NDVI tiles still painted in 3 districts (Шпаковский,
 * центр Ставрополь, Кочубеевский) which are not inside Руно. Those were
 * company-scope tiles painted before the farm switch; their cache entries
 * survived `setTiles()`.
 *
 * Affected sources (all 3 fixed by this helper):
 *   - `ndvi-active-raster`   — NDVI/NDVI_CONTRAST/GNR active overlay
 *   - `rgb-scene-raster`     — Sentinel-2 TCI true-colour scene
 *   - `cloud-mask-raster`    — cloud-mask overlay
 *
 * Pattern is identical to AK-118 `recreateMvtSource` (sibling file). Kept as
 * a separate module rather than generalising into one helper to:
 *   1. Avoid churn on AK-118 tests / wire-points that already shipped.
 *   2. Keep the type signature precise — raster sources do not have
 *      `sourceLayer` / `promoteId`; vector sources do. Shared signature would
 *      need a union + `type` discriminator, expanding the change surface
 *      beyond a focused hotfix.
 *
 * Order of operations (CRITICAL — MapLibre forbids removing a source while
 * layers still reference it):
 *
 *     1. snapshot all layer specs whose `source` matches the target id
 *        AND capture each layer's `beforeId` (= id of the next layer in the
 *        current style that is NOT itself being removed)
 *     2. removeLayer(...) each (in order)
 *     3. removeSource(target id)
 *     4. addSource(target id, raster spec with new tiles)
 *     5. addLayer(spec, beforeId) for each captured layer, preserving the
 *        z-order it had before the recreate
 *
 * No-op if the source does not exist (defensive — avoids throwing during
 * teardown / when a scope-change event fires after unmount).
 *
 * AK-147 fix (2026-05-23 founder UAT): without `beforeId`, MapLibre appends
 * re-added layers to the END of `style.layers`, putting them on TOP of every
 * other layer. Visible symptom: TCI satellite snapshot (declared at z-index 1,
 * intentionally UNDER field outlines / labels) jumps to the top of the stack
 * after a date / scope swap and visually covers contours. Capturing the
 * `beforeId` per layer restores the original ordering. Removed-set exclusion
 * is required because layers from the SAME source are removed together — the
 * naive "next layer in style.layers" would dangle on a sibling that is also
 * about to disappear.
 */
import type {
  LayerSpecification,
  Map as MaplibreMap,
  RasterSourceSpecification,
  SourceSpecification,
} from 'maplibre-gl';

/**
 * Raster source spec accepted by the helper. We force `type: 'raster'` and
 * inject `tiles` from `newUrl`; callers may omit `tileSize` (defaults to 256
 * — matches the convention used by `rasterSourceEffect.ts` registration).
 *
 * `tiles` provided by the caller is intentionally ignored — see test
 * "ignores tiles[] supplied in the spec" for the regression guard rationale.
 */
export type RasterSourceSpecWithoutTiles = Omit<
  RasterSourceSpecification,
  'tiles' | 'tileSize'
> & {
  tiles?: string[];
  tileSize?: number;
};

export function recreateRasterSource(
  map: MaplibreMap,
  sourceId: string,
  newUrl: string,
  spec: RasterSourceSpecWithoutTiles,
): void {
  // Defensive: source might not exist yet (initial-mount race) or might
  // already be torn down (unmount race). In either case there is nothing
  // to evict.
  if (!map.getSource(sourceId)) return;

  // Snapshot layer specs referencing this source BEFORE any removeLayer call.
  // For each captured layer we ALSO record its z-order anchor (`beforeId`) so
  // that step 4 can re-insert it at the exact same position.
  const style = map.getStyle();
  const allLayers = (style.layers ?? []) as LayerSpecification[];
  const targetLayers = allLayers.filter(
    (l): l is LayerSpecification =>
      'source' in l && (l as { source?: string }).source === sourceId,
  );
  const removedIds = new Set(targetLayers.map((l) => l.id));
  const layerPlacements: { spec: LayerSpecification; beforeId: string | undefined }[] =
    targetLayers.map((layer) => {
      const idx = allLayers.findIndex((l) => l.id === layer.id);
      // First subsequent layer that is NOT also being removed → our anchor.
      // If none exists, the layer was at (or moved to) the top, so beforeId
      // stays undefined and addLayer appends to the end as before.
      const next = allLayers.slice(idx + 1).find((l) => !removedIds.has(l.id));
      return { spec: { ...layer }, beforeId: next?.id };
    });

  // Step 1: removeLayer for each.
  for (const { spec } of layerPlacements) {
    if (map.getLayer(spec.id)) {
      map.removeLayer(spec.id);
    }
  }

  // Step 2: removeSource — evicts the entire (sourceId, z, x, y) tile cache.
  map.removeSource(sourceId);

  // Step 3: addSource with the NEW URL. Strip any caller-supplied `tiles` and
  // inject `[newUrl]` so the eviction guarantee cannot be broken by a stale
  // copy/paste. Default tileSize → 256 to match the project's raster
  // registration convention.
  const { tiles: _ignored, tileSize, ...rest } = spec;
  const newSpec: SourceSpecification = {
    ...rest,
    type: 'raster',
    tiles: [newUrl],
    tileSize: tileSize ?? 256,
  } as SourceSpecification;
  map.addSource(sourceId, newSpec);

  // Step 4: re-add layer specs in original order AND at their original
  // z-position via the captured `beforeId` anchor. Without the anchor,
  // MapLibre would append every re-added layer to the END of style.layers,
  // putting raster overlays on top of contours/labels (AK-147).
  //
  // Edge case: the anchor layer might have been removed by a concurrent
  // teardown between snapshot and re-insert. MapLibre throws on unknown
  // `beforeId`. We fall back to a plain addLayer (append) in that case —
  // the same behaviour as pre-AK-147, which is at worst a cosmetic glitch
  // and never a crash.
  for (const { spec, beforeId } of layerPlacements) {
    if (beforeId !== undefined && !map.getLayer(beforeId)) {
      map.addLayer(spec);
    } else {
      map.addLayer(spec, beforeId);
    }
  }
}
